import { useState, useCallback, useMemo } from "react";
import { Call } from "@twilio/voice-sdk";
import { useTellescopeQuery, useTellescopeInfiniteQuery, useSecureFileUrl } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { useCalling } from "../../hooks/calling";
import { ActiveCallMeta, CallDirection, CallStatus, ConferenceState } from "../../store";

export interface Participant {
  id: string;
  name: string;
  phone?: string;
  avatar?: string;
  type: "user" | "enduser" | "phone";
}

export interface UseCallWindowReturn {
  // Window visibility
  isVisible: boolean;

  // Call state
  activeCall: Call | null;
  callStatus: CallStatus;
  callDirection: CallDirection | null;
  isMuted: boolean;
  activeCallMeta: ActiveCallMeta;

  // Incoming calls
  incomingCalls: Call[];
  hasIncomingCalls: boolean;

  // Conference state
  conference: ConferenceState | null;
  isInConference: boolean;

  // Participants for display
  participants: Participant[];

  // UI state
  showAddAttendees: boolean;
  setShowAddAttendees: (show: boolean) => void;
  showDialpad: boolean;
  setShowDialpad: (show: boolean) => void;

  // Device state
  isConnected: boolean;
  isInitialized: boolean;

  // Actions
  handleAnswer: (call: Call) => void;
  handleReject: (call: Call) => void;
  handleHangUp: () => void;
  handleToggleMute: () => void;
  handleSendDigits: (digits: string) => void;
  handleAddAttendeeByUser: (userId: string, userTwilioNumber?: string) => Promise<boolean>;
  handleAddAttendeeByPhone: (phone: string) => Promise<boolean>;
  handleRemoveAttendee: (id: string, isPhone: boolean) => Promise<boolean>;
  handleUpgradeToConference: () => Promise<boolean>;
  handleEndConference: () => Promise<boolean>;

  // Call status label
  getCallStatusLabel: () => string;
}

/**
 * Hook for managing CallWindow UI state and actions.
 *
 * Combines the useCalling hook with additional UI-specific state
 * for the call window component.
 *
 * @example
 * ```tsx
 * function CallWindow() {
 *   const {
 *     isVisible,
 *     activeCall,
 *     callStatus,
 *     participants,
 *     handleAnswer,
 *     handleHangUp,
 *     ...
 *   } = useCallWindow();
 *
 *   if (!isVisible) return null;
 *
 *   return (
 *     <DraggableWindow open={isVisible} title="Phone Call">
 *       ...
 *     </DraggableWindow>
 *   );
 * }
 * ```
 */
export function useCallWindow(): UseCallWindowReturn {
  const calling = useCalling();

  // UI state
  const [showAddAttendees, setShowAddAttendees] = useState(false);
  const [showDialpad, setShowDialpad] = useState(false);

  // Window should be visible when there's an active call or incoming calls
  const isVisible = useMemo(() => {
    return calling.activeCall !== null || calling.hasIncomingCalls;
  }, [calling.activeCall, calling.hasIncomingCalls]);

  // Fetch enduser data for avatar and name display
  const enduserId = calling.activeCallMeta.enduserId;
  const phoneNumber = calling.activeCallMeta.phoneNumber;

  // Primary: fetch by enduser ID (available for regular inbound calls)
  const enduserByIdQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: { id: enduserId ?? "" },
    enabled: !!enduserId,
  });

  // Fallback: fetch by phone number (for queue calls where eId may not be set initially)
  const enduserByPhoneQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: { filter: { phone: phoneNumber ?? "" }, limit: 1 },
    enabled: !enduserId && !!phoneNumber,
    select: (data) => data.pages.flat(),
  });

  const enduser = enduserByIdQuery.data ?? enduserByPhoneQuery.data?.[0] ?? null;
  const enduserAvatarUrl = useSecureFileUrl(enduser?.avatar);

  // Resolve the enduser's display name from the fetched data, falling back to call meta
  const enduserDisplayName = useMemo(() => {
    if (enduser) {
      const name = formatName(enduser);
      if (name) return name;
    }
    return calling.activeCallMeta.enduserName ?? null;
  }, [enduser, calling.activeCallMeta.enduserName]);

  // Build participants list for display
  const participants = useMemo((): Participant[] => {
    const result: Participant[] = [];

    // Add the enduser from active call meta
    if (calling.activeCallMeta.enduserId || calling.activeCallMeta.phoneNumber) {
      result.push({
        id: calling.activeCallMeta.enduserId ?? calling.activeCallMeta.phoneNumber ?? "unknown",
        name: enduserDisplayName ?? calling.activeCallMeta.phoneNumber ?? "Unknown",
        phone: calling.activeCallMeta.phoneNumber ?? undefined,
        avatar: enduserAvatarUrl,
        type: calling.activeCallMeta.enduserId ? "enduser" : "phone",
      });
    }

    // Add conference participants
    if (calling.conference) {
      // Add users
      calling.conference.users.forEach((userId) => {
        result.push({
          id: userId,
          name: userId, // TODO: Resolve user name from user store
          type: "user",
        });
      });

      // Add phone numbers
      calling.conference.phones.forEach((phone) => {
        result.push({
          id: phone,
          name: phone,
          type: "phone",
        });
      });
    }

    return result;
  }, [calling.activeCallMeta, calling.conference, enduserAvatarUrl, enduserDisplayName]);

  // Action handlers - wrap calling methods with any needed UI logic
  const handleAnswer = useCallback(
    (call: Call) => {
      calling.answerCall(call);
      // Close add attendees panel when answering
      setShowAddAttendees(false);
    },
    [calling]
  );

  const handleReject = useCallback(
    (call: Call) => {
      calling.rejectCall(call);
    },
    [calling]
  );

  const handleHangUp = useCallback(() => {
    calling.hangUp();
    // Reset UI state
    setShowAddAttendees(false);
    setShowDialpad(false);
  }, [calling]);

  const handleToggleMute = useCallback(() => {
    calling.toggleMute();
  }, [calling]);

  const handleSendDigits = useCallback(
    (digits: string) => {
      calling.sendDigits(digits);
    },
    [calling]
  );

  const handleAddAttendeeByUser = useCallback(
    async (userId: string, userTwilioNumber?: string): Promise<boolean> => {
      // First upgrade to conference if not already
      if (!calling.isInConference) {
        const upgraded = await calling.upgradeToConference();
        if (!upgraded) return false;
      }
      return calling.addAttendeeByUser(userId, userTwilioNumber);
    },
    [calling]
  );

  const handleAddAttendeeByPhone = useCallback(
    async (phone: string): Promise<boolean> => {
      // First upgrade to conference if not already
      if (!calling.isInConference) {
        const upgraded = await calling.upgradeToConference();
        if (!upgraded) return false;
      }
      return calling.addAttendeeByPhone(phone);
    },
    [calling]
  );

  const handleRemoveAttendee = useCallback(
    async (id: string, isPhone: boolean): Promise<boolean> => {
      return calling.removeAttendee(id, isPhone);
    },
    [calling]
  );

  const handleUpgradeToConference = useCallback(async (): Promise<boolean> => {
    return calling.upgradeToConference();
  }, [calling]);

  const handleEndConference = useCallback(async (): Promise<boolean> => {
    const result = await calling.endConference();
    // Reset UI state
    setShowAddAttendees(false);
    setShowDialpad(false);
    return result;
  }, [calling]);

  return {
    // Window visibility
    isVisible,

    // Call state (pass through from useCalling)
    activeCall: calling.activeCall,
    callStatus: calling.callStatus,
    callDirection: calling.callDirection,
    isMuted: calling.isMuted,
    activeCallMeta: calling.activeCallMeta,

    // Incoming calls
    incomingCalls: calling.incomingCalls,
    hasIncomingCalls: calling.hasIncomingCalls,

    // Conference state
    conference: calling.conference,
    isInConference: calling.isInConference,

    // Participants
    participants,

    // UI state
    showAddAttendees,
    setShowAddAttendees,
    showDialpad,
    setShowDialpad,

    // Device state
    isConnected: calling.isConnected,
    isInitialized: calling.isInitialized,

    // Actions
    handleAnswer,
    handleReject,
    handleHangUp,
    handleToggleMute,
    handleSendDigits,
    handleAddAttendeeByUser,
    handleAddAttendeeByPhone,
    handleRemoveAttendee,
    handleUpgradeToConference,
    handleEndConference,

    // Status label
    getCallStatusLabel: calling.getCallStatusLabel,
  };
}
